import { createSlice } from "@reduxjs/toolkit";
import {
  createAssistant,
  deleteAssistant,
  fetchAssistantById,
  fetchAssistants,
  fetchDoctorRendezvousAsAssistant,
  loginAssistant,
  resendAssistantInvitation,
  setAssistantActivationPassword,
  updateAssistant,
  verifyAssistantActivationToken,
} from "../thunks/assistantManagement.thunks";

const initialState = {
  byId: {},
  allIds: [],
  listLoading: false,
  listError: null,
  detailLoading: false,
  detailError: null,
  mutationLoading: false,
  mutationError: null,
  activation: {
    verifyLoading: false,
    verifyError: null,
    verifyData: null,
    setPasswordLoading: false,
    setPasswordError: null,
    setPasswordSuccess: false,
  },
  auth: {
    loading: false,
    error: null,
    isAuthenticated: false,
    accessToken: null,
    assistantId: null,
    doctorId: null,
  },
  doctorRendezvous: {
    items: [],
    loading: false,
    error: null,
  },
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.assistants)) return value.assistants;
  return [];
};

const normalizeAssistant = (assistant) => {
  if (!assistant) return null;
  const id = String(assistant.id ?? assistant._id ?? "").trim();
  if (!id) return null;

  return {
    ...assistant,
    id,
    fullName: String(assistant.fullName ?? assistant.name ?? "").trim(),
    email: String(assistant.email ?? "").trim().toLowerCase(),
    phone: String(assistant.phone ?? "").trim(),
    status: String(assistant.status ?? "").trim().toLowerCase() || "unknown",
  };
};

const upsertAssistant = (state, assistant) => {
  const normalized = normalizeAssistant(assistant);
  if (!normalized) return;
  state.byId[normalized.id] = {
    ...(state.byId[normalized.id] || {}),
    ...normalized,
  };
  if (!state.allIds.includes(normalized.id)) {
    state.allIds.push(normalized.id);
  }
};

const replaceAssistants = (state, payload) => {
  state.byId = {};
  state.allIds = [];
  asArray(payload).forEach((assistant) => upsertAssistant(state, assistant));
};

const assistantManagementSlice = createSlice({
  name: "assistantManagement",
  initialState,
  reducers: {
    resetAssistantManagementState: () => initialState,
    clearAssistantAuthState: (state) => {
      state.auth = {
        loading: false,
        error: null,
        isAuthenticated: false,
        accessToken: null,
        assistantId: null,
        doctorId: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAssistants.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchAssistants.fulfilled, (state, action) => {
        state.listLoading = false;
        replaceAssistants(state, action.payload);
      })
      .addCase(fetchAssistants.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload?.message || "Failed to fetch assistants";
      })

      .addCase(fetchAssistantById.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchAssistantById.fulfilled, (state, action) => {
        state.detailLoading = false;
        upsertAssistant(state, action.payload);
      })
      .addCase(fetchAssistantById.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload?.message || "Failed to fetch assistant";
      })

      .addCase(createAssistant.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
      })
      .addCase(createAssistant.fulfilled, (state, action) => {
        state.mutationLoading = false;
        upsertAssistant(state, action.payload);
      })
      .addCase(createAssistant.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload?.message || "Failed to create assistant";
      })

      .addCase(updateAssistant.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
      })
      .addCase(updateAssistant.fulfilled, (state, action) => {
        state.mutationLoading = false;
        upsertAssistant(state, action.payload);
      })
      .addCase(updateAssistant.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload?.message || "Failed to update assistant";
      })

      .addCase(resendAssistantInvitation.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
      })
      .addCase(resendAssistantInvitation.fulfilled, (state) => {
        state.mutationLoading = false;
      })
      .addCase(resendAssistantInvitation.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload?.message || "Failed to resend invitation";
      })

      .addCase(deleteAssistant.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
      })
      .addCase(deleteAssistant.fulfilled, (state, action) => {
        state.mutationLoading = false;
        const id = String(
          action.payload?.assistantId ?? action.payload?.id ?? ""
        ).trim();
        if (!id) return;
        delete state.byId[id];
        state.allIds = state.allIds.filter(
          (assistantId) => String(assistantId).trim() !== id
        );
      })
      .addCase(deleteAssistant.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload?.message || "Failed to delete assistant";
      })

      .addCase(verifyAssistantActivationToken.pending, (state) => {
        state.activation.verifyLoading = true;
        state.activation.verifyError = null;
      })
      .addCase(verifyAssistantActivationToken.fulfilled, (state, action) => {
        state.activation.verifyLoading = false;
        state.activation.verifyData = action.payload;
      })
      .addCase(verifyAssistantActivationToken.rejected, (state, action) => {
        state.activation.verifyLoading = false;
        state.activation.verifyError =
          action.payload?.message || "Failed to verify activation token";
      })

      .addCase(setAssistantActivationPassword.pending, (state) => {
        state.activation.setPasswordLoading = true;
        state.activation.setPasswordError = null;
        state.activation.setPasswordSuccess = false;
      })
      .addCase(setAssistantActivationPassword.fulfilled, (state) => {
        state.activation.setPasswordLoading = false;
        state.activation.setPasswordSuccess = true;
      })
      .addCase(setAssistantActivationPassword.rejected, (state, action) => {
        state.activation.setPasswordLoading = false;
        state.activation.setPasswordError =
          action.payload?.message || "Failed to set assistant password";
      })

      .addCase(loginAssistant.pending, (state) => {
        state.auth.loading = true;
        state.auth.error = null;
      })
      .addCase(loginAssistant.fulfilled, (state, action) => {
        state.auth.loading = false;
        state.auth.error = null;
        state.auth.isAuthenticated = Boolean(action.payload?.accessToken);
        state.auth.accessToken = action.payload?.accessToken || null;
        state.auth.assistantId = action.payload?.assistantId || null;
        state.auth.doctorId = action.payload?.doctorId || null;
      })
      .addCase(loginAssistant.rejected, (state, action) => {
        state.auth.loading = false;
        state.auth.error = action.payload?.message || "Failed to login assistant";
        state.auth.isAuthenticated = false;
      })

      .addCase(fetchDoctorRendezvousAsAssistant.pending, (state) => {
        state.doctorRendezvous.loading = true;
        state.doctorRendezvous.error = null;
      })
      .addCase(fetchDoctorRendezvousAsAssistant.fulfilled, (state, action) => {
        state.doctorRendezvous.loading = false;
        state.doctorRendezvous.items = asArray(action.payload);
      })
      .addCase(fetchDoctorRendezvousAsAssistant.rejected, (state, action) => {
        state.doctorRendezvous.loading = false;
        state.doctorRendezvous.error =
          action.payload?.message || "Failed to fetch doctor rendez-vous";
      });
  },
});

export const { resetAssistantManagementState, clearAssistantAuthState } =
  assistantManagementSlice.actions;

export default assistantManagementSlice.reducer;
